<template>
  <div class="payment-container">
    <div class="payment-header">
      <div class="logo-wrapper">
        <img src="@/assets/logo.png" alt="QUALI-D" class="logo" />
      </div>
      <h2 class="payment-title">Complete Your Payment</h2>
      <p class="payment-subtitle">Please complete the {{ paymentType }} process in the secure iframe below</p>
    </div>

    <div class="payment-content">
      <div v-if="loading" class="loading-state">
        <div class="spinner-large"></div>
        <p>Loading payment gateway...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <div class="error-icon">⚠️</div>
        <h3>Payment Error</h3>
        <p>{{ error }}</p>
        <button @click="retryPayment" class="retry-button">Try Again</button>
      </div>

      <div v-else class="iframe-container">
        <iframe
          :src="paymentUrl"
          class="payment-iframe"
          frameborder="0"
          @load="onIframeLoad"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation"
        ></iframe>
      </div>

      <div class="payment-status" v-if="!paymentSuccess && !error">
        <div class="status-indicator">
          <div class="spinner"></div>
          <span>Waiting for payment to be processed...</span>
        </div>
      </div>
    </div>

    <!-- Success Modal removed - now redirecting to dedicated success pages -->
  </div>
</template>

<script>
import { PaymentsService } from '../services/payments.js'

export default {
  name: 'PaymentView',
  data () {
    return {
      paymentUrl: '',
      merchantRefNum: '',
      loading: true,
      error: '',
      checkingPayment: false,
      paymentSuccess: false,
      statusCheckInterval: null,
      maxStatusChecks: 180, // Maximum 15 minutes (180 checks * 5 seconds)
      statusCheckCount: 0,
      isRenewal: false
    }
  },
  computed: {
    paymentType () {
      return this.isRenewal ? 'renewal payment' : 'payment'
    }
    // Removed unused success-related computed properties since we now redirect to dedicated pages
  },
  mounted () {
    this.initializePayment()
    this.startAutomaticStatusCheck()
  },
  beforeUnmount () {
    this.stopAutomaticStatusCheck()
  },
  methods: {
    initializePayment () {
      this.paymentUrl = this.$route.query.url
      this.merchantRefNum = this.$route.query.merchantRefNum || localStorage.getItem('merchantRefNum')
      this.isRenewal = this.$route.query.renewal === 'true' || this.$route.meta.isRenewal

      if (!this.paymentUrl || !this.merchantRefNum) {
        this.error = `Invalid payment parameters. Please try ${this.isRenewal ? 'renewing' : 'registering'} again.`
        this.loading = false
        return
      }

      console.log('Payment initialized with URL:', this.paymentUrl)
      console.log('Merchant Reference:', this.merchantRefNum)
      console.log('Is Renewal:', this.isRenewal)

      this.loading = false
    },
    onIframeLoad () {
      console.log('Payment iframe loaded')
    },
    startAutomaticStatusCheck () {
      // Check payment status every 5 seconds
      this.statusCheckInterval = setInterval(() => {
        this.checkPaymentStatus(true) // Silent check
      }, 5000)
    },
    stopAutomaticStatusCheck () {
      if (this.statusCheckInterval) {
        clearInterval(this.statusCheckInterval)
        this.statusCheckInterval = null
      }
    },
    async checkPaymentStatus (silent = false) {
      if (!this.merchantRefNum) {
        console.error('No merchant reference number available')
        return
      }

      if (!silent) {
        this.checkingPayment = true
      }

      try {
        console.log('Checking payment status for merchant ref:', this.merchantRefNum)

        const response = await fetch(`https://payments.quali-d.com/api/payment-status/${this.merchantRefNum}`)
        const data = await response.json()

        console.log('Payment status response:', data)

        if (response.ok) {
          const orderStatus = data.orderStatus || 'FAILED'
          console.log('Received order status:', orderStatus)

          // Define valid payment statuses
          const validStatuses = ['NEW', 'UNPAID', 'PAID', 'CANCELED', 'REFUNDED', 'EXPIRED', 'PARTIAL_REFUNDED', 'FAILED']
          const finalizedStatuses = ['PAID', 'CANCELED', 'REFUNDED', 'EXPIRED', 'PARTIAL_REFUNDED', 'FAILED']

          // Validate the status
          const validatedStatus = validStatuses.includes(orderStatus) ? orderStatus : 'FAILED'
          console.log('Validated status:', validatedStatus)

          if (validatedStatus === 'PAID') {
            // Payment successful - now confirm with backend
            this.stopAutomaticStatusCheck()
            await this.confirmPaymentWithBackend()
            console.log('Payment successful!')
          } else if (finalizedStatuses.includes(validatedStatus) && validatedStatus !== 'PAID') {
            // Payment has reached a final state but not successful
            this.stopAutomaticStatusCheck()
            this.redirectToFailurePage(validatedStatus, data.error?.description)
            console.log(`Payment finalized with status: ${validatedStatus}`)
          }
          // For statuses like 'NEW', 'UNPAID', continue checking
          console.log('Continuing to check payment status...')
        } else {
          console.error('Failed to check payment status:', response.status, response.statusText)
        }

        // Stop checking after maximum attempts
        this.statusCheckCount++
        if (this.statusCheckCount >= this.maxStatusChecks) {
          this.stopAutomaticStatusCheck()
          if (!this.paymentSuccess && !this.error) {
            this.redirectToFailurePage('timeout', 'Payment verification timeout after 15 minutes. Please contact support if your payment was completed.')
          }
        }
      } catch (error) {
        console.error('Error checking payment status:', error)
        if (!silent) {
          this.error = 'Failed to check payment status. Please try again.'
        }
      } finally {
        if (!silent) {
          this.checkingPayment = false
        }
      }
    },
    async confirmPaymentWithBackend () {
      try {
        let confirmResult

        if (this.isRenewal) {
          confirmResult = await PaymentsService.confirmRenewal(this.merchantRefNum)
        } else {
          confirmResult = await PaymentsService.confirmPayment(this.merchantRefNum)
        }

        if (confirmResult.success) {
          // Clean up
          localStorage.removeItem('merchantRefNum')

          // Redirect to success page
          if (this.isRenewal) {
            this.$router.push('/renewal-success')
          } else {
            const userQuery = confirmResult.user ? `?user=${encodeURIComponent(JSON.stringify(confirmResult.user))}` : ''
            this.$router.push(`/success${userQuery}`)
          }
        } else {
          this.redirectToFailurePage('system_error', confirmResult.message)
        }
      } catch (error) {
        console.error('Error confirming payment:', error)
        this.redirectToFailurePage('system_error', 'Failed to confirm payment with backend')
      }
    },

    redirectToFailurePage (status, description) {
      const errorParams = new URLSearchParams({
        status: status,
        ref: this.merchantRefNum,
        type: this.isRenewal ? 'renewal' : 'registration'
      })

      if (description) {
        errorParams.set('description', description)
      }

      this.$router.push(`/payment-failed?${errorParams.toString()}`)
    },

    handleSuccess () {
      // This method is now replaced by confirmPaymentWithBackend
      // Keeping for backward compatibility if called elsewhere
      this.confirmPaymentWithBackend()
    }
  }
}
</script>

<style scoped>
.payment-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  display: flex;
  flex-direction: column;
  padding: 20px;
}

.payment-header {
  text-align: center;
  color: white;
  margin-bottom: 32px;
}

.logo-wrapper {
  margin-bottom: 16px;
}

.logo {
  height: 48px;
  width: auto;
}

.payment-title {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
}

.payment-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
}

.payment-content {
  flex: 1;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  position: relative;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
}

.spinner-large {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-state h3 {
  color: #dc2626;
  margin: 0 0 8px 0;
}

.error-state p {
  color: #6b7280;
  margin-bottom: 24px;
}

.retry-button {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

.iframe-container {
  width: 100%;
  height: 600px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;
}

.payment-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.payment-status {
  margin-bottom: 24px;
  padding: 16px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #0369a1;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #bae6fd;
  border-top: 2px solid #0369a1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Success Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 16px;
  padding: 48px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
}

.success-modal {
  border-top: 4px solid #10b981;
}

.success-icon {
  width: 80px;
  height: 80px;
  background: #10b981;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: bold;
  margin: 0 auto 24px;
}

.success-title {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
}

.success-message {
  color: #6b7280;
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 32px 0;
}

.success-button {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
}

.success-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 768px) {
  .payment-content {
    padding: 24px 16px;
  }

  .iframe-container {
    height: 500px;
  }

  .payment-actions {
    flex-direction: column;
  }

  .check-button,
  .cancel-button {
    width: 100%;
  }

  .modal-container {
    margin: 16px;
    padding: 32px 24px;
  }
}
</style>
